import { ComboboxItem } from "@mantine/core"
import { useThrottledCallback } from "@mantine/hooks"
import { DefaultSelectProps, DefaultSelect } from "../DefaultSelect"
import { useDefaultSuspenseInfiniteQuery } from "../../utils/useDefaultSuspenseInfiniteQuery"
import { Pagination } from "../../utils/Pagination"

type ExtractType<TData> = TData extends Pagination<infer T> ? T : never

export interface SelectFromInfiniteQueryProps<
  TQueryData extends ReturnType<typeof useDefaultSuspenseInfiniteQuery>,
  TData = TQueryData extends ReturnType<
    typeof useDefaultSuspenseInfiniteQuery<any, infer T extends Pagination<any>>
  >
    ? ExtractType<T>
    : unknown,
> extends Omit<
    DefaultSelectProps,
    "options" | "searchValue" | "onSearchChange"
  > {
  queryData: TQueryData
  entitiesToComboboxData: (data: TData) => ComboboxItem
  searchValue?: string
  onSearchChange?: (val: string) => void
  loading?: boolean
}

export function SelectFromInfiniteQuery<
  TQueryData extends ReturnType<typeof useDefaultSuspenseInfiniteQuery>,
  TData = TQueryData extends ReturnType<
    typeof useDefaultSuspenseInfiniteQuery<any, infer T extends Pagination<any>>
  >
    ? ExtractType<T>
    : unknown,
>({
  searchValue,
  onSearchChange,
  entitiesToComboboxData,
  loading,
  queryData,
  ...rest
}: SelectFromInfiniteQueryProps<TQueryData, TData>) {
  const [internalSearchValue, setInternalSearchValue] = React.useState(
    searchValue ?? ""
  )

  React.useEffect(() => {
    setInternalSearchValue(searchValue ?? "")
  }, [searchValue])

  const { data, hasNextPage, isFetchingNextPage, isLoading, fetchNextPage } =
    queryData

  const throttledFetchNextPage = useThrottledCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, 300)

  const handleSearchValueChange = (newVal: string) => {
    onSearchChange?.(newVal)
    setInternalSearchValue(newVal)
  }

  return (
    <DefaultSelect
      searchValue={internalSearchValue}
      onSearchChange={handleSearchValueChange}
      options={
        data?.pages.flatMap((page) =>
          page?.data.flatMap((entity) => entitiesToComboboxData(entity))
        ) ?? []
      }
      value={rest.value}
      loading={isLoading || loading}
      onScrolledToBottom={throttledFetchNextPage}
      {...rest}
    />
  )
}
